import React, { useState } from "react";
import Dropdown from "../Dropdown";

/**
 * PBI #297: "see another user's rights and request the same for a new one". The orderer
 * picks a colleague as the model, sees that person's Entra groups pre-ticked, adjusts the
 * set, and the result travels in the SCO case mail for Diflex to act on.
 *
 * The status matters as much as the list. An empty list cannot tell a customer who
 * genuinely has no groups from one whose administrator has not yet approved Insight's
 * access to read them, and those two need different words: the first is nothing to say,
 * the second is something the orderer can act on. A missing status means the fetch itself
 * failed, and there the picker disappears rather than blocking onboarding.
 */

/** Wording per sync status. Anything not listed leaves the picker out entirely. */
const STATUS_MESSAGES = {
  PermissionDenied:
    "Group memberships cannot be shown yet: an administrator in your organisation has to " +
    "approve Insight's access to read your directory groups. Describe any group needs in " +
    "the comment field until then.",
  NeverRun:
    "Group memberships have not been read from your directory yet. Describe any group " +
    "needs in the comment field.",
  Failed:
    "Group memberships could not be read from your directory. Describe any group needs in " +
    "the comment field.",
  SyncDisabled:
    "Group memberships could not be read from your directory. Describe any group needs in " +
    "the comment field.",
  // #353: a sync that found nothing assignable (every group rule-based, or none at all)
  // still gets its section, so the orderer sees why there is nothing to pick.
  Succeeded:
    "No assignable groups were found in your directory — groups with rule-based " +
    "membership cannot be assigned by hand. Describe any group needs in the comment field.",
};

/**
 * #298: distribution lists and Microsoft 365 groups sit among the permission groups, named
 * for what they are so the orderer can tell a mailing list from an access group. A plain
 * security group needs no mark.
 */
const GROUP_KIND_LABELS = {
  DistributionList: "distribution list",
  MailEnabledSecurity: "security group with email",
  Microsoft365: "Microsoft 365 group",
};

/**
 * Marks a group where its membership changes what the orderer is actually asking for. A
 * group synced from on-premises AD cannot be changed in Entra at all, and a role-assignable
 * group hands out a directory role — neither should pass as an ordinary checkbox.
 */
const groupLabel = (group) => {
  const name = group.displayName || group.entraGroupPublicId;
  const marks = [];

  const kind = GROUP_KIND_LABELS[group.groupSource];
  if (kind) marks.push(kind);

  // Both can be true, and the second is the one nobody should miss — so they compose
  // rather than the first winning, as they do in the case mail.
  if (group.isOnPremisesSynced) marks.push("change in AD");
  if (group.isAssignableToRole) marks.push("grants an administrator role");

  return marks.length > 0 ? `${name} (${marks.join(", ")})` : name;
};

/**
 * "as of 2026-09-08 23:27" in the viewer's own time — so "the group we created this morning is
 * missing" is explainable rather than mysterious. One format for every browser language (#355).
 * The API sends UTC timestamps without an offset, which a browser would read as local time, so
 * a missing offset is taken to mean UTC. An unparseable value simply yields no age line.
 */
const describeSnapshot = (asOfUtc) => {
  if (!asOfUtc) return null;

  const hasOffset = /(Z|[+-]\d{2}:?\d{2})$/i.test(asOfUtc);
  const asOf = new Date(hasOffset ? asOfUtc : `${asOfUtc}Z`);
  if (Number.isNaN(asOf.getTime())) return null;

  const pad = (number) => String(number).padStart(2, "0");
  const date = `${asOf.getFullYear()}-${pad(asOf.getMonth() + 1)}-${pad(asOf.getDate())}`;
  const time = `${pad(asOf.getHours())}:${pad(asOf.getMinutes())}`;

  return `Group information as of ${date} ${time}.`;
};

/**
 * The line under the model-user dropdown. It names the chosen colleague rather than
 * leaving that to the dropdown: the hub's searchable Dropdown does not echo its selected
 * value in the closed button, so without this the orderer cannot see who they copied from.
 */
const modelUserHelp = (membershipsLoading, modelUserLabel) => {
  if (membershipsLoading) return "Reading that user's group memberships...";

  if (modelUserLabel)
    return `Copied from ${modelUserLabel}. Adjust the ticks below as needed.`;

  return "Optional. Ticks the groups that user belongs to, so you can adjust them instead of picking from scratch.";
};

/** With more groups than this the list gets a search box. */
const SEARCH_THRESHOLD = 8;

/**
 * #364: every group as one always-visible list with a tick each. A user can sit in 50 groups;
 * a dropdown hides what a copied colleague's groups add up to behind a closed button that shows
 * only the first few names, while this list shows every tick, lets each be removed on its own,
 * and states how many are chosen.
 */
const GroupChecklist = ({ groups, selected, onChange }) => {
  const [query, setQuery] = useState("");
  const needle = query.trim().toLowerCase();
  const visible = needle
    ? groups.filter((group) => groupLabel(group).toLowerCase().includes(needle))
    : groups;

  const toggle = (groupPublicId) =>
    onChange(
      selected.includes(groupPublicId)
        ? selected.filter((value) => value !== groupPublicId)
        : [...selected, groupPublicId]
    );

  return (
    <div className="flex flex-col w-full">
      <p className="block text-sm font-bold text-gray-800 mb-2">
        Group memberships
        <span className="font-normal text-gray-600">
          {" "}
          · {selected.length} of {groups.length} selected
        </span>
      </p>

      {groups.length > SEARCH_THRESHOLD && (
        <input
          type="text"
          aria-label="Search groups"
          placeholder="Search groups"
          className="input-default w-full mb-2"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      )}

      <div className="max-h-72 overflow-y-auto border rounded-md">
        {visible.map((group) => (
          <label
            key={group.entraGroupPublicId}
            className="flex items-center py-2 px-3 hover:bg-blue-50 cursor-pointer"
          >
            <input
              type="checkbox"
              className="custom-checkbox-input hidden"
              checked={selected.includes(group.entraGroupPublicId)}
              onChange={() => toggle(group.entraGroupPublicId)}
            />
            <div className="custom-checkbox mr-2">
              <svg
                className="custom-checkbox-icon"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            {groupLabel(group)}
          </label>
        ))}
        {visible.length === 0 && (
          <p className="text-sm text-gray-500 px-3 py-2">No groups match the search.</p>
        )}
      </div>
    </div>
  );
};

const GroupMembershipPicker = ({
  status = "",
  asOfUtc = null,
  options = [],
  selected = [],
  onChange = () => {},
  userLabels = [],
  modelUserLabel = "",
  // Onboarding copies from a colleague; editing an existing user has nobody to copy from,
  // so the block is left out when no handler is given (#372).
  onModelUserChange,
  membershipsLoading = false,
}) => {
  const groups = Array.isArray(options) ? options : [];
  const message = STATUS_MESSAGES[status];

  // A missing status means the fetch itself failed; then the picker stays out of the way
  // rather than blocking onboarding. Every known status keeps the section heading (#353) —
  // a bare sentence under the previous field read as "the section is missing".
  if (groups.length === 0 && !message) return null;

  if (groups.length === 0) {
    return (
      <div className="flex flex-col w-full">
        <p className="block text-sm font-bold text-gray-800 mb-2">Group memberships</p>
        <p className="text-sm text-gray-600">{message}</p>
      </div>
    );
  }

  const snapshot = describeSnapshot(asOfUtc);

  return (
    <div className="flex flex-col w-full gap-4">
      {onModelUserChange && (
        <div className="flex flex-col w-full">
          <Dropdown
            label="Copy group memberships from"
            value={modelUserLabel || ""}
            options={Array.isArray(userLabels) ? userLabels : []}
            onSelect={onModelUserChange}
            placeholder="Choose user"
            searchable={true}
          />
          <p className="text-sm text-gray-600 mt-1">
            {modelUserHelp(membershipsLoading, modelUserLabel)}
          </p>
        </div>
      )}

      <div className="flex flex-col w-full">
        <GroupChecklist
          groups={groups}
          selected={Array.isArray(selected) ? selected : []}
          onChange={onChange}
        />
        {snapshot && <p className="text-sm text-gray-600 mt-1">{snapshot}</p>}
      </div>
    </div>
  );
};

export default GroupMembershipPicker;
